import { z } from 'zod'

/**
 * A market is the *rest* of the question, and it has to be a small closed set.
 *
 * "Where does this rank" is meaningless without a country, a language and a
 * device, and the provider will accept any of them — including free-form
 * location strings down to a suburb. Accepting that would be the end of the
 * shared cache: two tenants asking about the same keyword from the same country
 * would miss each other because one of them typed a city. So the axes are
 * enumerated and interned, and a city-level market is a later decision made
 * once with its cost understood, rather than a text input.
 */
export const SERP_DEVICES = ['desktop', 'mobile'] as const
export type SerpDevice = (typeof SERP_DEVICES)[number]

/** Google's `gl`. Two-letter regions, lowercase, as the provider expects. */
export const SERP_COUNTRIES = [
  'gb',
  'us',
  'ie',
  'ca',
  'au',
  'nz',
  'de',
  'fr',
  'es',
  'it',
  'nl',
  'be',
  'pt',
  'se',
  'no',
  'dk',
  'fi',
  'pl',
  'at',
  'ch',
  'br',
  'mx',
  'in',
  'za',
  'ae',
  'sg',
  'jp',
] as const
export type SerpCountry = (typeof SERP_COUNTRIES)[number]

/** Google's `hl`. Fewer than the countries on purpose: the interface offers a
 * language for the market, not every locale Google will parse. */
export const SERP_LANGUAGES = [
  'en',
  'de',
  'fr',
  'es',
  'it',
  'nl',
  'pt',
  'sv',
  'no',
  'da',
  'fi',
  'pl',
  'ja',
] as const
export type SerpLanguage = (typeof SERP_LANGUAGES)[number]

export const serpMarketSchema = z.object({
  gl: z.enum(SERP_COUNTRIES),
  hl: z.enum(SERP_LANGUAGES),
  device: z.enum(SERP_DEVICES),
})

export type SerpMarket = z.infer<typeof serpMarketSchema>

/**
 * What a site is checked in until it says otherwise, and `us` for a reason that
 * is about the provider rather than about anybody's audience.
 *
 * Measured live across six countries on one query: `gl=us` answers with the
 * feature blocks — people also ask, related searches, the knowledge panel — and
 * `gb`, `au`, `ca`, `ie` and `de` answer with organic results and nothing else.
 * So a non-US default silently makes `serp_feature` an empty table and takes
 * "what else is on the page" off the board for every site at once, where a US
 * default gets it wrong only for the sites that then say so. It is a floor to
 * override, not a claim about where anyone's readers are: `site_serp_config`
 * is where a site picks its own, and every read and every new star takes it
 * from there.
 */
export const SERP_DEFAULT_MARKET: SerpMarket = { gl: 'us', hl: 'en', device: 'desktop' }

/** Stable, readable, and short enough to be a queue's singleton key. */
export function serpMarketKey(market: SerpMarket): string {
  return `${market.gl}-${market.hl}-${market.device}`
}

/**
 * The interned form of a keyword.
 *
 * Lowercased and internally collapsed, which is further than `normalizePageUrl`
 * goes and is safe here for the reason it is not safe there: Google does not
 * distinguish "Best CRM" from "best crm", and Search Console already hands us
 * every query lowercased. Trailing and doubled whitespace is the difference
 * between a keyword pasted from a spreadsheet and the same keyword typed, and
 * keeping them apart would buy two checks for one question.
 */
export function normalizeKeyword(input: string): string {
  return input.trim().replace(/\s+/g, ' ').toLowerCase()
}

export const serpKeywordSchema = z
  .string()
  .min(1, 'Enter a keyword')
  .max(200)
  .transform(normalizeKeyword)
  .refine((value) => value.length > 0, 'Enter a keyword')
